import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDocument, mixpanelSetup } from '../functions';
import { isAdminUserId } from '../utils/userFlags';

const UserContext = createContext(null);

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loggedIn, setLoggedIn] = useState(null);

    const userIdRef = useRef(null);

    useEffect(() => {
        const auth = getAuth();

        // Set up auth state listener to update context when login/logout happens
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // Skip token-refresh / multi-tab re-fires for the same user — nothing to redo
            if (firebaseUser && firebaseUser.uid === userIdRef.current) {
                return;
            }

            setLoading(true);

            if (firebaseUser) {
                // User is logged in
                userIdRef.current = firebaseUser.uid;
                const providerData = firebaseUser.providerData;
                let emailChangeable = true;
                let authMethod = null;
                
                // Determine auth method definitively from providerData
                for (let index = 0; index < providerData.length; index++) {
                    const providerId = providerData[index].providerId;
                    if (providerId !== "password") {
                        emailChangeable = false;
                    }
                    // Check for Google OAuth provider
                    if (providerId === "google.com") {
                        authMethod = 'google';
                    }
                    // Check for email/password provider (only set if Google not found)
                    else if (providerId === "password" && authMethod !== 'google') {
                        authMethod = 'email';
                    }
                }
                
                // Store auth method in localStorage if detected
                if (authMethod) {
                    localStorage.setItem('lastAuthMethod', authMethod);
                }
                
                const authResult = {
                    status: 200,
                    userId: firebaseUser.uid,
                    email: firebaseUser.email,
                    emailVerified: firebaseUser.emailVerified,
                    emailChangeable: emailChangeable,
                    user: firebaseUser
                };
                
                setUser(authResult);
                setLoggedIn(true);

                // Check admin status
                setIsAdmin(isAdminUserId(authResult.userId));

                // Fetch user document
                const colRef = "users";
                const queryData = await getDocument(colRef, authResult.userId);
                if (queryData.status === 200) {
                    const userDocData = queryData.data.data;
                    setUserData(userDocData);

                    // Fire-and-forget analytics SDK setup — must not block first paint
                    const email = userDocData.email;
                    const name = `${userDocData.firstName} ${userDocData.lastName}`;
                    mixpanelSetup(email, name, authResult.userId);
                } else {
                    // Doc not found yet (e.g. fresh signup before the background
                    // write lands). Don't clobber a locally-hydrated userData;
                    // genuine logout clears via the outer (no firebaseUser) branch.
                    setUserData(prev => prev || null);
                }
            } else {
                // User is logged out
                userIdRef.current = null;
                setLoggedIn(false);
                setUser(null);
                setUserData(null);
                setIsAdmin(false);
            }
            
            setLoading(false);
        });

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, []);

    const refreshUser = useCallback(async () => {
        if (user?.userId) {
            const colRef = "users";
            const queryData = await getDocument(colRef, user.userId, true); // Force refresh
            if (queryData.status === 200) {
                setUserData(queryData.data.data);
                return queryData.data.data;
            }
        }
        return null;
    }, [user?.userId]);

    const hydrateUserData = useCallback((nextUserData) => {
        if (!nextUserData || typeof nextUserData !== 'object') return;
        setUserData(prev => ({ ...(prev || {}), ...nextUserData }));
    }, []);

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        user,
        userData,
        isAdmin,
        loading,
        loggedIn,
        refreshUser,
        hydrateUserData
    }), [user, userData, isAdmin, loading, loggedIn, refreshUser, hydrateUserData]);

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};
